import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllQuotations, deleteQuotation, fetchPDF } from "../../services/QuotationServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildQuotationQueryParams } from "../../utils/listViewDateRanges";
import moment from 'moment';
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatQty, formatAmount, sumField } from "../../utils/listSummaryUtils";

function QuotationTable() {
    const InitialData = { records: [] };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const left_pinned = ['quotation_no'];
    const right_pinned = ['id'];

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const summaryTotals = useMemo(() => {
        const todayRecords = data.records.filter(
            (row) => row.quotation_date === todayDateLabel
        );
        return {
            total_count: data.records.length,
            today_count: todayRecords.length,
            total_qty: data.records.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
            total_amount: data.records.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
        };
    }, [data.records, todayDateLabel]);

    const highlightCard = useMemo(
        () => buildGradientCard('Today Quote', summaryTotals.today_count, 'today'),
        [summaryTotals.today_count],
    );

    const summaryCards = useMemo(() => ([
        buildGradientCard('Total', summaryTotals.total_count, 'blue'),
        buildGradientCard('Total Qty', formatQty(summaryTotals.total_qty), 'purple'),
        buildGradientCard('Total Amount', formatAmount(summaryTotals.total_amount), 'teal'),
    ]), [summaryTotals]);

    const columns = useMemo(
        () => [
            { fieldName: 'company_id', headerName: 'Company', size: 80 },
            { fieldName: 'quotation_no', headerName: 'Quote No', size: 88 },
            { fieldName: 'quotation_date', headerName: 'Date', size: 86 },
            { fieldName: 'customer_id', headerName: 'Customer', size: 120 },
            { fieldName: 'project_name', headerName: 'Project', size: 110 },
            { fieldName: 'product_id', headerName: 'Grade', size: 75 },
            { fieldName: 'quantity', headerName: 'Qty', size: 68, align: 'right' },
            { fieldName: 'rate', headerName: 'Rate', size: 68, align: 'right' },
            { fieldName: 'amount', headerName: 'Amount', size: 80, align: 'right' },
            { fieldName: 'tax', headerName: 'Tax', size: 70 },
            { fieldName: 'concrete_structure', headerName: 'Structure', size: 95 },
            { fieldName: 'delivery_mode', headerName: 'Delivery', size: 82 },
            { fieldName: 'salesrep_id', headerName: 'Marketing', size: 100 },
            { fieldName: 'status', headerName: 'Status', size: 72 },
            { fieldName: 'id', headerName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const loadQuotations = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllQuotations(cookies, buildQuotationQueryParams(config))
            .then((quotationList) => {
                const tableData = (quotationList.quotation_list || []).map((quotation) => ({
                    id: quotation.id,
                    company_id: quotation.company?.alias ?? '',
                    product_id: quotation.product?.name ?? '',
                    quotation_no: `${quotation.prefix || ''}${quotation.quotation_no || ''}`,
                    quotation_date: quotation.quotation_date,
                    customer_id: quotation.customer?.name ?? '',
                    salesrep_id: quotation.customer?.salesrep?.name ?? '',
                    consignee_id: quotation.consignee?.name ?? '',
                    quantity: quotation.quantity,
                    rate: quotation.rate,
                    amount: quotation.amount,
                    status: quotation.status ? 'active' : 'inactive',
                    project_name: quotation.consignee?.project_name ?? '',
                    tax: quotation.tax?.name ?? '',
                    concrete_structure: quotation.concrete_structure?.name ?? '',
                    delivery_mode: (quotation.delivery_mode ?? '').toUpperCase(),
                }));
                setData({ records: tableData });
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load quotation list');
                setData({ records: [] });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadQuotations(config);
    }, [loadQuotations]);

    const onEditQuotation = (quotationId) => {
        navigate(`/QuotationEdit/${quotationId}/`);
    };

    const onDeleteQuotation = (quotationId, event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Are you sure to Delete?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                deleteQuotation(cookies, quotationId)
                    .then(() => {
                        Swal.fire('Deleted Successfully!', '', 'success');
                        setRefreshKey((oldKey) => oldKey + 1);
                    })
                    .catch((error) => {
                        setIsLoading(false);
                        displayError(error?.response?.data ?? error.message, 'Delete Failed');
                    });
            } else if (result.isDismissed) {
                Swal.fire('Not Deleted', '', 'info');
            }
        });
    };

    const handlePrint = (quotationId, event) => {
        event.preventDefault();
        setIsLoading(true);
        fetchPDF(cookies, quotationId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Quotation',
                    downloadFileName: 'quotation.pdf',
                });
            })
            .catch((error) => {
                displayError(error, 'Custom print/export failed');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS').map((c) => c.headerName),
            data.records.map((quotationcsv) => ({
                company_id: quotationcsv.company_id,
                product_id: quotationcsv.product_id,
                quotation_no: quotationcsv.quotation_no,
                quotation_date: quotationcsv.quotation_date,
                customer_id: quotationcsv.customer_id,
                salesrep_id: quotationcsv.salesrep_id,
                quantity: quotationcsv.quantity,
                rate: quotationcsv.rate,
                amount: quotationcsv.amount,
                status: quotationcsv.status,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Quotation',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records,
            'quotation.pdf',
        );
    };

    useEffect(() => {
        getListViewSettings(cookies, 'quotation')
            .then((response) => {
                loadQuotations(response.config || {});
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load list settings');
                setIsLoading(false);
            });
    }, [cookies, loadQuotations, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="MARKETING"
                    title="List of Quotation"
                    viewKey="quotation"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/Quotation"
                    addNewLabel="New Quotation"
                />

                <ListPageGradientSummary highlightCard={highlightCard} cards={summaryCards} />

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF QUOTATION"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditQuotation}
                            onRowDelete={onDeleteQuotation}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete
                            onRowPrint={handlePrint}
                            hidePrint={false}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            compactRows
                        />
                    </div>
                </div>
            </div>
            <button
                style={{ position: 'relative', right: '40px' }}
                className="btn btn-fill btn-primary float-end"
                type="button"
                onClick={() => { navigate('/Quotation'); }}
            >
                Back
            </button>
        </div>
    );
}

export default QuotationTable;
